/**
 * Directional slide swap when question content key changes.
 *
 * Belongs here: enter/exit animation layering (outgoing snapshot + incoming slide).
 * Direction is set by useContentTransition before the key changes.
 * Optional duration overrides stay presentational (CSS vars + matching JS clear timeout).
 */
import { type ReactNode, useEffect, useRef, useState } from 'react'
import {
  contentSlideDurationStyle,
  resolveContentSlideOutDurationMs
} from './contentTransitionTimings'

export enum SlideDirection {
  Forward = 'forward',
  Back = 'back'
}

type OutgoingSnapshot = {
  node: ReactNode
  animationClassName: 'animate-content-slide-out-left' | 'animate-content-slide-out-right'
}

interface DirectionalSlideSwapProps {
  contentKey: string
  direction: SlideDirection
  children: ReactNode
  /** Override slide-in duration (ms). Defaults to CSS `--content-transition-duration`. */
  inDurationMs?: number
  /** Override slide-out duration (ms). Defaults to CSS `--content-transition-out-duration`. */
  outDurationMs?: number
}

/** CSS enter class used by DirectionalSlideSwap — reuse so layout shells stay in sync. */
export function getContentSlideInClassName(direction: SlideDirection): string {
  if (direction === SlideDirection.Back) return 'animate-content-slide-in-from-left'
  return 'animate-content-slide-in'
}

/** CSS exit class for directional slide-out. */
export function getContentSlideOutClassName(direction: SlideDirection): OutgoingSnapshot['animationClassName'] {
  if (direction === SlideDirection.Forward) return 'animate-content-slide-out-left'
  return 'animate-content-slide-out-right'
}

/** Directional enter/exit swap when contentKey changes. Enter plays on mount and remount. */
export function DirectionalSlideSwap({
  contentKey,
  direction,
  children,
  inDurationMs,
  outDurationMs
}: DirectionalSlideSwapProps) {
  const previousContentKeyRef = useRef(contentKey)
  const previousChildrenRef = useRef(children)
  const [outgoing, setOutgoing] = useState<OutgoingSnapshot | null>(null)
  const outDurationResolvedMs = resolveContentSlideOutDurationMs(outDurationMs)

  if (previousContentKeyRef.current !== contentKey) {
    // Capture outgoing children before React replaces them — drives exit animation
    setOutgoing({
      node: previousChildrenRef.current,
      animationClassName: getContentSlideOutClassName(direction)
    })
    previousContentKeyRef.current = contentKey
  }
  previousChildrenRef.current = children

  useEffect(() => {
    if (!outgoing) return
    const timeoutId = window.setTimeout(() => setOutgoing(null), outDurationResolvedMs)
    return () => window.clearTimeout(timeoutId)
  }, [outgoing, outDurationResolvedMs])

  const slideInClassName = getContentSlideInClassName(direction)

  return (
    <div
      className='relative overflow-hidden'
      style={contentSlideDurationStyle({ inMs: inDurationMs, outMs: outDurationMs })}
    >
      {outgoing ? (
        <div className='absolute inset-0 z-0 pointer-events-none' aria-hidden>
          <div className={outgoing.animationClassName}>{outgoing.node}</div>
        </div>
      ) : null}
      <div
        key={contentKey}
        className={outgoing ? `relative z-10 ${slideInClassName}` : slideInClassName}
      >
        {children}
      </div>
    </div>
  )
}

interface ContentSlideOutProps {
  direction: SlideDirection
  children: ReactNode
  /**
   * When false, renders a stable shell with no animation (keeps children mounted).
   * When true, applies leave class — flip without remounting children (avoids blink + re-enter).
   */
  active?: boolean
  /** Optional — leave is often visual-only; phase advance may use a separate timer. */
  onComplete?: () => void
  outDurationMs?: number
}

/**
 * One-shot directional leave — used when content exits without an incoming peer
 * (e.g. last question before stage-complete Rive).
 * Keep mounted with `active={false|true}` so children are not remounted when leave starts.
 */
export function ContentSlideOut({
  direction,
  children,
  active = true,
  onComplete,
  outDurationMs
}: ContentSlideOutProps) {
  return (
    <div
      className='relative overflow-hidden'
      style={active ? contentSlideDurationStyle({ outMs: outDurationMs }) : undefined}
    >
      <div
        className={active ? getContentSlideOutClassName(direction) : undefined}
        onAnimationEnd={(event) => {
          if (!active) return
          // Ignore bubbled animationend from descendants
          if (event.target !== event.currentTarget) return
          onComplete?.()
        }}
      >
        {children}
      </div>
    </div>
  )
}
